import React, { Fragment, useState, useRef, useEffect } from 'react'
import Conditional from '../Conditional'
import { useFilters } from '../FilterBar/hooks/useFilters'
import {
  FilterCategory,
  FilterOption,
  FilterOptions,
  OnChangeMetaValue,
} from '../FilterBar/queries/types'
import RawFilterBar from '../FilterBar/unmanaged'
import { WithOutsideTracking } from '../hocs'
import { SearchBar } from '../Inputs'
import CheckBox from '../Inputs/CheckBox/index'
import DateRangePicker from '../Inputs/DatePicker/DateRangePicker'
import { CallbackProps } from '../Inputs/DatePicker/Popover'
import { kuiperClassNames } from '../utils'
import './styles.scss'

export type ContentFilterProps = {
  additionalFilterOptions?: FilterOptions
  contentOptions: boolean
  currentMomentId?: number
  currentStoryUuid?: string
  disabledFilterCategories?: Array<FilterCategory>
  endDate: Date
  initialSelectedFilters?: Array<string>
  onChange: (
    activeFilters: Array<FilterOption>,
    metaValue: OnChangeMetaValue
  ) => void
  placeholder: string
  searchTerms: string
  startDate: Date
  toggleIncludeContentOptions: (value: boolean) => void
  updateEndDate: (date: CallbackProps) => void
  updateSearchTerms: (value: string, name: string | undefined) => void
  updateStartDate: (date: CallbackProps) => void
  withContentOptions: boolean
  withDateRange: boolean
  withSearch: boolean
  workspaceId: number
}

export const ContentFilter: React.VFC<ContentFilterProps> = ({
  additionalFilterOptions,
  contentOptions,
  currentMomentId,
  currentStoryUuid,
  disabledFilterCategories,
  endDate,
  initialSelectedFilters,
  onChange,
  placeholder,
  searchTerms,
  startDate,
  toggleIncludeContentOptions,
  updateEndDate,
  updateSearchTerms,
  updateStartDate,
  withContentOptions,
  withDateRange = true,
  withSearch = true,
  workspaceId,
}) => {
  const [filterSearchFocus, setFilterSearchFocus] = useState(false)
  const searchBarRef = useRef(null)

  // Certain pages will automatically set a filter for the user
  // Here we'll build out that initial filter value from props
  const initialSelected = React.useMemo(() => {
    const initialFilterProp =
      initialSelectedFilters?.map((filter) => ({
        id: filter,
        disabled: false,
      })) ?? []

    if (typeof currentStoryUuid === 'string') {
      return [
        { id: `story-${currentStoryUuid}`, disabled: false },
        ...initialFilterProp,
      ]
    }

    if (typeof currentMomentId === 'number') {
      return [
        { id: `moment-${currentMomentId}`, disabled: false },
        ...initialFilterProp,
      ]
    }

    return initialFilterProp.length > 0 ? initialFilterProp : undefined
  }, [currentMomentId, currentStoryUuid, initialSelectedFilters])

  const {
    currentPath,
    disableAll,
    getSelectedOptions,
    lastPath,
    loading,
    onSearchChange,
    options,
    removeAllFilters,
    removeFilter,
    searchTerm,
    selected,
    selectFilter,
    toggleAllFilters,
    toggleFilter,
    updateFilterNavigation,
  } = useFilters({
    additionalFilterOptions,
    disabledCategories: disabledFilterCategories,
    initialSelected,
    workspaceId,
  })

  const focusFilterSearchBar = () => setFilterSearchFocus(true)

  const blurFilterSearchBar = () => {
    if (!filterSearchFocus) return

    setFilterSearchFocus(false)
  }

  /**
   * Call onChange as filter values are updated.
   * Argument shape and naming conventions preserved from the
   * original filterBar implementation to perserve compatibility.
   */
  useEffect(() => {
    // Make sure we don't fire change events before filters are all loaded
    if (loading) return

    // All selected FilterOption objects
    const activeFilters = getSelectedOptions()
    const metaValue: OnChangeMetaValue = {
      // State of global filter disable toggle
      disabled: disableAll,
      value: {
        // All selected filter ids, including those that are disabled
        selected: selected.map((opt) => opt.id) || [],
        // Set of filters that are individually disabled
        disabled:
          selected.filter((opt) => opt.disabled).map((opt) => opt.id) || [],
        // Object containing values of date filters
        filterValues: selected
          .filter((opt) => opt.disabled || opt.value === undefined)
          .reduce((acc, opt) => ({ ...acc, [opt.id]: opt.value }), {}),
      },
    }
    onChange?.(activeFilters, metaValue)
    // Intentionally removing function references from the deps array
    // We only want this to run when the relevant values change, not
    // when function references update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableAll, selected, loading])

  return (
    <Fragment>
      <Conditional condition={withDateRange}>
        <Fragment>
          <div
            className={kuiperClassNames('cf-label input-label no-validation')}
            id="date-range-picker-label"
          >
            DATES
          </div>
          <DateRangePicker
            ariaLabelledBy={'date-range-picker-label'}
            startDate={startDate}
            endDate={endDate}
            onChangeStartDate={updateStartDate}
            onChangeEndDate={updateEndDate}
          />
        </Fragment>
      </Conditional>
      <Conditional condition={withSearch}>
        <Fragment>
          <div
            className={kuiperClassNames('cf-label input-label no-validation')}
            id="search-bar-label"
          >
            SEARCH
          </div>
          <SearchBar
            ariaLabelledBy={'search-bar-label'}
            className={kuiperClassNames('cf-search')}
            icon="search"
            inputType="search"
            onChange={updateSearchTerms}
            placeholder={placeholder}
            value={searchTerms}
          />
        </Fragment>
      </Conditional>
      <Conditional condition={withContentOptions}>
        <Fragment>
          <CheckBox
            theme="dark"
            value={contentOptions}
            onChange={toggleIncludeContentOptions}
            label="Include content data"
          />
        </Fragment>
      </Conditional>
      <div className={kuiperClassNames('cf-label input-label no-validation')}>
        FILTERS
      </div>
      <div
        className={kuiperClassNames('cf-filter-bar', {
          'cf-focus': filterSearchFocus,
        })}
      >
        <WithOutsideTracking
          onOutsideClick={blurFilterSearchBar}
          getTrackingContext={() => searchBarRef.current}
        >
          <RawFilterBar
            currentPath={currentPath}
            disabled={disableAll}
            disabledValues={selected
              .filter(({ disabled }) => disabled)
              .map(({ id }) => id)}
            filterTree={options}
            focused={true}
            lastPath={lastPath}
            loading={loading}
            onClearFilters={removeAllFilters}
            onDisableAll={toggleAllFilters}
            onDisableFilter={toggleFilter}
            onFilterRemove={removeFilter}
            onFilterSelect={selectFilter}
            onFocus={focusFilterSearchBar}
            onPathChange={updateFilterNavigation}
            onSearch={onSearchChange}
            searchBarRef={searchBarRef}
            searchTerm={searchTerm}
            strictSearchFocus={true}
            theme="dark"
            value={selected.map(({ id }) => id)}
          />
        </WithOutsideTracking>
      </div>
    </Fragment>
  )
}
